import React, { useEffect, useState } from 'react';
import mammoth from 'mammoth';
import { 
  Box, 
  Button, 
  Card, 
  Grid, 
  IconButton, 
  LinearProgress, 
  Paper, 
  Typography 
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import * as pdfjsLib from 'pdfjs-dist';
import FileIconRenderer from './FileIconRenderer';

pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url
).toString();

function RecipeFileUpload({
  setRecipeContent,
  setRecipeData,
  selectedFile,
  setSelectedFile,
  isProcessing,
  setIsProcessing
}) {

  // Function to handle PDF parsing
  const handlePdfParse = async (file) => {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      let fullText = '';

      // Get text from all pages
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const textContent = await page.getTextContent();
        const pageText = textContent.items.map(item => item.str).join(' ');
        fullText += pageText + '\n';
      }

      return fullText;
    } catch (error) {
      console.error('Error parsing PDF:', error);
      throw error;
    }
  };

  // Function to handle Word document parsing
  const handleDocxParse = async (file) => {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const result = await mammoth.extractRawText({ arrayBuffer });
      return result.value;
    } catch (error) {
      console.error('Error parsing DOCX:', error);
      throw error;
    }
  };

  // Modified file upload handler
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setIsProcessing(true);
    setSelectedFile(file);

    try {
      let text = '';
      if (file.type === 'application/pdf') {
        text = await handlePdfParse(file);
      } else if (file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
        text = await handleDocxParse(file);
      }

      // Send to your backend which will forward to ChatGPT
      const response = await fetch(
        `${process.env.REACT_APP_API_PATH}api/openai/parse-recipe`, 
        {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          fileData: text,
          fileName: file.name,
          fileType: file.type,
        })
      });

      if (!response.ok) {
        throw new Error('Failed to parse recipe');
      }

      const recipeData = await response.json();
      const {
        title,
        tags,
        ingredients,
        instructions,
        prepTime,
        cookTime,
        totalTime,
        servings,
        difficulty
      } = recipeData;

      // Update form with parsed data
      setRecipeData(prev => ({
        ...prev,
        title: title,
        tags: tags,
        ingredients: ingredients,
        recipeContent: instructions,
        prepTime: prepTime,
        cookTime: cookTime,
        totalTime: totalTime,
        servings: servings,
        difficulty: difficulty
      }));

      setRecipeContent(instructions);

    } catch (error) {
      console.error('Error processing file:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Paper elevation={3} sx={{ p: 2 }}>
      {(selectedFile && !isProcessing) ? 
        (
          <Card sx={{ padding: '1rem', display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'space-between' }}>
            <Grid display={'flex'} width={'10rem'} justifyContent={'space-between'}>
              <FileIconRenderer fileType={selectedFile.type}/>
              <Typography variant="h6">{selectedFile.name}</Typography>
            </Grid>
            <IconButton 
              size="md" 
              onClick={() => {
                setSelectedFile(null);
                setRecipeData(prev => ({
                  ...prev,
                  title: '',
                  recipeContent: ''
                }));
                setRecipeContent('');
              }}
              disabled={isProcessing}
            >
              <DeleteIcon />
            </IconButton>
          </Card>
        ) : (
          <Button
            variant="contained"
            component="label"
            startIcon={<CloudUploadIcon />}
            fullWidth
            disabled={isProcessing}
          >
            {isProcessing ? 'Processing...' : 'Upload Recipe File'}
            <input
              type="file"
              hidden
              onChange={handleFileUpload}
              accept=".pdf,.doc,.docx"
            />
          </Button>
        ) 
      }
      {isProcessing && (
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <LinearProgress sx={{marginBottom: '1rem'}}/>
          <Typography variant="body2" color="text.secondary">
            Analyzing document... Please wait
          </Typography>
        </Box>
      )}
    </Paper>
  )
}

export default RecipeFileUpload;